import React, { JSX, memo, useCallback, useMemo, useRef, useState } from 'react'
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  SectionList,
  SectionListData,
  View,
} from 'react-native'
import { Divider } from '@ui-kitten/components'
import { useSelector } from 'react-redux'
import { isEqual } from 'lodash'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleService, useStyleSheet } from '@src/style/service'
import { ListInjectedProps } from '@src/components/list/types'
import { PlaceholderView, CommonRefreshControl } from '@components'
import { ItemType, getListItem } from '@src/components/list/ListItemFactory'
import { SearchBar } from '@src/components/SearchBar'
import { ListQuickFilter } from '@src/containers/ListQuickFilter'
import { withDataProvider } from '@src/components/list/DataProviderList'
import { ListFooter } from '@src/components/list/common/ListFooter'
import { SectionHeader } from '@src/components/SectionHeader'
import { eventsSortSelector } from '@src/selectors/events'
import { isModified } from '@src/navigation/utils'
import { EventsItemType } from '../models/events.types'
import { EVENTS_DEFAULT_SORT_STATE } from '../models/constants'
import { CollapsibleTimeline } from './CollapsibleTimeline'

export interface EventsListProps extends ListInjectedProps {
  items: SectionListData<any, { title: string; index: number }>[]
  filters: any
  sort: any
  query: string
  onQueryUpdated: (text: string) => void
  ListFooterComponent: React.ReactNode
  finishedLoadingFooterComponent: React.ReactNode
  onViewableItemsChanged: (info: any) => void
  showSearchBar: boolean
  showEventsListFilters?: boolean
  emptyViewPlaceholder?: React.ReactElement | null
  collapseChartOnScroll?: boolean
  headerComponent?: JSX.Element
  useInsets?: boolean
  ChartComponent: React.ReactNode
}

const keyExtractor = (item: EventsItemType) => item.id

const EventsListComponent = (props: EventsListProps) => {
  const [chartExpanded, setChartExpanded] = useState(true)

  const secondSectionRef = useRef<View>(null)
  const charWrapperRef = useRef<View>(null)
  const chartBottomThresholdRef = useRef(0)
  const eventsSort = useSelector(eventsSortSelector)

  const styles = useStyleSheet(themedStyle)
  const {
    items,
    initialNumToRender,
    refreshing,
    isLoadingMore,
    finishedLoadingFooterComponent,
    onListScroll,
    onSortUpdated,
    onEndReached,
    onRefresh,
    sort,
    filters,
    query,
    onViewableItemsChanged,
    onQueryUpdated,
    listRef,
    showSearchBar,
    showEventsListFilters = true,
    emptyViewPlaceholder = null,
    collapseChartOnScroll = true,
    headerComponent,
    ChartComponent,
    useInsets = false,
  } = props

  const chartVisible = useMemo(() => {
    // the chart is always visible if the collapse on scroll is disabled
    if (!collapseChartOnScroll) {
      return true
    }
    return isEqual(eventsSort, EVENTS_DEFAULT_SORT_STATE)
  }, [collapseChartOnScroll, eventsSort])

  const handleChartCollapse = useCallback(
    (_event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (charWrapperRef.current) {
        charWrapperRef.current.measure((_x, _y, _width, height, _pageX, pageY) => {
          chartBottomThresholdRef.current = height + pageY
        })
      }

      if (secondSectionRef.current) {
        secondSectionRef.current.measure((_x, _y, _width, _height, _pageX, pageY) => {
          if (pageY < chartBottomThresholdRef.current && chartExpanded) {
            setChartExpanded(false)
          } else if (pageY > chartBottomThresholdRef.current && !chartExpanded) {
            setChartExpanded(true)
          }
        })
      }
    },
    [chartExpanded],
  )

  const renderSectionHeader = useCallback(
    ({
      section: { title, adornment },
    }: {
      section: { title: string; adornment?: React.ReactNode }
    }) => {
      return (
        <View
          // important for the view to not be removed by Android;
          // if this view is removed the chart will not collapse anymore
          collapsable={false}
          ref={title === items[1]?.title ? secondSectionRef : null}
          style={styles.sectionHeaderContainer}
        >
          <SectionHeader title={title} />
          {adornment && <View style={styles.sectionAdornment}>{adornment}</View>}
        </View>
      )
    },
    [items, styles.sectionAdornment, styles.sectionHeaderContainer],
  )

  const refreshControl = useMemo(
    () => <CommonRefreshControl refreshing={refreshing} onRefresh={onRefresh} />,
    [onRefresh, refreshing],
  )

  const listEmptyComponent = useMemo(
    () =>
      emptyViewPlaceholder ? (
        <View style={styles.emptyViewPlaceholder}>{emptyViewPlaceholder}</View>
      ) : (
        <PlaceholderView showSearchResults={isModified(sort)} type="events" />
      ),
    [emptyViewPlaceholder, sort, styles.emptyViewPlaceholder],
  )

  const listFooterComponent = useMemo(
    () => (
      <ListFooter
        isLoadingMore={isLoadingMore}
        isListEmpty={items.length === 0}
        finishedLoadingComponent={finishedLoadingFooterComponent}
        footerStyles={{ marginBottom: 90 }}
      />
    ),
    [finishedLoadingFooterComponent, isLoadingMore, items.length],
  )

  const listQuickFilterComponent = (
    <View style={styles.quickFilters}>
      <ListQuickFilter
        filters={filters}
        sort={sort}
        useNestedQuickFilters
        onSortUpdated={onSortUpdated}
      />
    </View>
  )

  const insets = useSafeAreaInsets()

  const contentContainerStyle = useMemo(
    () => (useInsets ? { paddingBottom: insets.bottom + 16 } : undefined),
    [insets.bottom, useInsets],
  )

  const renderItem = useCallback(({ item }: { item: ItemType }) => getListItem(item), [])

  return (
    <View style={styles.container}>
      {showEventsListFilters && listQuickFilterComponent}
      <Divider style={!showEventsListFilters && styles.divider} />
      {showSearchBar && (
        <SearchBar testID="EventsList/SearchBar" value={query} onChangeText={onQueryUpdated} />
      )}
      <View style={styles.container}>
        {!!ChartComponent && chartVisible && (
          <View
            // important for the view to not be removed by Android;
            // if this view is removed the chart will not collapse anymore
            collapsable={false}
            ref={charWrapperRef}
          >
            <CollapsibleTimeline expanded={chartExpanded} ChartComponent={ChartComponent} />
          </View>
        )}
        <SectionList
          ref={listRef}
          sections={items}
          keyExtractor={keyExtractor}
          initialNumToRender={initialNumToRender}
          stickySectionHeadersEnabled={false}
          renderItem={renderItem}
          renderSectionHeader={renderSectionHeader}
          refreshing={refreshing}
          onScroll={(event) => {
            !!ChartComponent && collapseChartOnScroll && handleChartCollapse(event)
            onListScroll?.(event)
          }}
          contentContainerStyle={contentContainerStyle}
          ListHeaderComponent={headerComponent}
          scrollEventThrottle={16}
          onEndReached={onEndReached}
          onViewableItemsChanged={onViewableItemsChanged}
          refreshControl={refreshControl}
          ListEmptyComponent={listEmptyComponent}
          ListFooterComponent={listFooterComponent}
        />
      </View>
    </View>
  )
}

export const EventsList = memo(withDataProvider(EventsListComponent))

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  quickFilters: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  divider: {
    marginVertical: 8,
  },
  sectionHeaderContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionAdornment: {
    paddingHorizontal: 16,
  },
  emptyViewPlaceholder: {
    paddingBottom: 16,
  },
})
